import { Note } from 'tonal';
import type { Chord } from '@/types/chord';
import type { Move, MoveContext, MoveResult } from '../types';

/**
 * let the bass hold — pedal point. Sets the bass of the next 1-2 chords
 * (v1 has no multi-select range, so "or to selection end" collapses to
 * just the next two) to the selected chord's own bass note.
 *
 * A pedal repeats the identical pitch, not just the pitch class, so there's
 * no transposition math here — Tonal is used only to guard against a
 * malformed note string on the source chord.
 */
export const letTheBassHold: Move = {
  id: 'let-the-bass-hold',
  name: 'let the bass hold',
  theory: 'pedal point',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    return !!chord && ctx.index + 1 < ctx.chords.length;
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const pedalBass = chord.voices.bass;
    if (Note.midi(pedalBass) === null) {
      throw new Error(`let-the-bass-hold: chord ${chord.id} has an invalid bass note "${pedalBass}"`);
    }

    const affectedCount = Math.min(2, ctx.chords.length - ctx.index - 1);
    const changedIds: string[] = [];

    const chords: Chord[] = ctx.chords.map((c, i) => {
      const offset = i - ctx.index;
      if (offset >= 1 && offset <= affectedCount) {
        changedIds.push(c.id);
        return {
          ...c,
          voices: { ...c.voices, bass: pedalBass },
          voicesCustomized: true,
        };
      }
      return c;
    });

    return {
      chords,
      changedIds,
      explanation: 'the ground note stays put while the harmony walks above it.',
      theoryLabel: 'pedal point',
    };
  },
};
